<script setup>
import { computed, ref } from 'vue';
import {
  grantAnalyticsConsent,
  hasAnalyticsConsent,
} from 'dashboard/helper/AnalyticsHelper/utils/trackingEnvelope';
import {
  resolveCookieConsentCopy,
  resolveCookieConsentCtaStyle,
} from './cookieConsentOptions';

const props = defineProps({
  enabled: { type: Boolean, default: true },
  policyLink: { type: String, default: '' },
  copy: { type: Object, default: () => ({}) },
  cta: { type: Object, default: () => ({}) },
});

const dismissed = ref(false);
const accepted = ref(hasAnalyticsConsent());

const isVisible = computed(
  () => props.enabled && !dismissed.value && !accepted.value
);
const consentCopy = computed(() =>
  resolveCookieConsentCopy({ copy: props.copy, cta: props.cta })
);
const acceptButtonStyle = computed(() =>
  resolveCookieConsentCtaStyle(props.cta)
);

const acceptCookies = () => {
  grantAnalyticsConsent();
  accepted.value = true;
};

const dismissCookies = () => {
  dismissed.value = true;
};
</script>

<template>
  <div>
    <aside
      v-if="isVisible"
      class="fixed inset-x-2 bottom-2 z-50 mx-auto flex max-w-[calc(100vw-1rem)] flex-col gap-3 rounded-lais border border-lais-border bg-lais-bg-card p-3 shadow-lais-pop sm:inset-x-3 sm:bottom-3 sm:max-w-4xl sm:gap-4 sm:p-4 md:inset-x-auto md:right-5 md:w-[520px]"
      role="dialog"
      aria-live="polite"
      :aria-label="consentCopy.title"
    >
      <div class="flex items-start gap-2.5 sm:gap-3">
        <span
          class="i-lucide-shield-check mt-0.5 size-4 shrink-0 text-lais-primary sm:size-5"
          aria-hidden="true"
        />
        <div class="min-w-0">
          <h2
            class="text-xs font-semibold leading-4 text-lais-text sm:text-sm sm:leading-5"
          >
            {{ consentCopy.title }}
          </h2>
          <p
            class="mt-1 text-xs leading-4 text-lais-text-muted sm:text-sm sm:leading-5"
          >
            {{ consentCopy.description }}
            <a
              v-if="policyLink"
              :href="policyLink"
              class="ml-1 font-semibold text-lais-link hover:text-lais-link-hover"
              target="_blank"
              rel="noopener noreferrer"
            >
              {{ consentCopy.policy }}
            </a>
          </p>
        </div>
      </div>
      <div class="grid grid-cols-2 gap-2 sm:flex sm:flex-row sm:justify-end">
        <button
          type="button"
          class="inline-flex items-center justify-center rounded-lais border border-lais-border bg-white px-3 py-2 text-xs font-semibold text-lais-text transition hover:border-lais-primary sm:px-4 sm:text-sm"
          @click="dismissCookies"
        >
          {{ consentCopy.close }}
        </button>
        <button
          type="button"
          class="inline-flex items-center justify-center gap-1.5 rounded-lais bg-lais-primary px-3 py-2 text-xs font-semibold text-white shadow-lais-soft transition hover:bg-lais-primary-600 sm:gap-2 sm:px-4 sm:text-sm"
          :style="acceptButtonStyle"
          @click="acceptCookies"
        >
          <span class="i-lucide-check size-3.5 sm:size-4" aria-hidden="true" />
          {{ consentCopy.accept }}
        </button>
      </div>
    </aside>
  </div>
</template>
